/**
 * Message Text Selection
 *
 * Browser helpers for preserving chat message text selection while custom
 * copy/reply actions are visible.
 *
 * Primary responsibilities:
 * - Identify native selections that belong to chat message text
 * - Mark selection action controls so they do not steal focus
 * - Coordinate one active message selection action group per chat view
 *
 * Dependencies:
 * - Browser Selection and DOM APIs
 */

export const MESSAGE_TEXT_SELECTION_ATTRIBUTE = 'data-message-text-selection'
export const MESSAGE_SELECTION_ACTIONS_ATTRIBUTE = 'data-message-selection-actions'
export const MESSAGE_TEXT_SELECTION_REPLACED_EVENT =
  'losi:message-text-selection-replaced'
export const MESSAGE_TEXT_SELECTION_CLEAR_EVENT =
  'losi:message-text-selection-clear'

export type MessageTextSelectionReplacedDetail = {
  ownerId: string
}

const messageTextSelectionSelector = `[${MESSAGE_TEXT_SELECTION_ATTRIBUTE}="true"]`
const messageSelectionActionsSelector = `[${MESSAGE_SELECTION_ACTIONS_ATTRIBUTE}="true"]`

// --------------

function getElementFromTarget(target: EventTarget | Node | null): Element | null {
  if (!target) return null
  if (target instanceof Element) return target
  if (target instanceof Node) return target.parentElement

  return null
}

// --------------

export function findMessageTextSelectionElement(
  target: EventTarget | Node | null
): Element | null {
  return getElementFromTarget(target)?.closest(messageTextSelectionSelector) ?? null
}

// --------------

export function isMessageSelectionActionTarget(target: EventTarget | Node | null) {
  return Boolean(
    getElementFromTarget(target)?.closest(messageSelectionActionsSelector)
  )
}

// --------------

export function isSelectionInsideMessageText(selection: Selection | null) {
  return Boolean(getMessageTextSelectionElement(selection))
}

// --------------

export function getMessageTextSelectionElement(selection: Selection | null) {
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return null
  }

  const anchorElement = findMessageTextSelectionElement(selection.anchorNode)
  const focusElement = findMessageTextSelectionElement(selection.focusNode)

  if (!anchorElement || !focusElement || anchorElement !== focusElement) {
    return null
  }

  return anchorElement
}

// --------------

export function shouldPreserveMessageTextSelection(target?: EventTarget | Node | null) {
  if (typeof document === 'undefined') return false
  if (target && isMessageSelectionActionTarget(target)) return true

  return isSelectionInsideMessageText(document.getSelection())
}

// --------------

export function dispatchMessageTextSelectionReplaced(ownerId: string) {
  if (typeof window === 'undefined') return

  window.dispatchEvent(
    new CustomEvent<MessageTextSelectionReplacedDetail>(
      MESSAGE_TEXT_SELECTION_REPLACED_EVENT,
      { detail: { ownerId } }
    )
  )
}

// --------------

export function dispatchMessageTextSelectionClear() {
  if (typeof window === 'undefined') return

  window.dispatchEvent(new CustomEvent(MESSAGE_TEXT_SELECTION_CLEAR_EVENT))
}
